import React from 'react';
import SpotlightCard from './SpotlightCard';

interface PhysicsListItem {
  id: string;
  [key: string]: any;
}

type SpacingType = 'comfortable' | 'compact';

interface InteractiveListProps<T extends PhysicsListItem> {
  items: T[];
  loading?: boolean;
  emptyMessage?: string;
  renderItem: (item: T, index: number) => React.ReactNode;
  onItemClick?: (item: T) => void;
  selectedItemId?: string | null;
  className?: string;
  enablePhysics?: boolean;
  variant?: 'surface' | 'flat';
  spacing?: SpacingType;
  itemPadding?: string;
}

const spacingPresets: Record<SpacingType, { container: string; item: string; rounded: string }> = {
  comfortable: {
    container: 'space-y-2',
    item: 'px-5 py-4',
    rounded: 'rounded-xl'
  },
  compact: {
    container: 'space-y-0',
    item: 'p-3',
    rounded: '!rounded-none'
  }
};

export function InteractiveList<T extends PhysicsListItem>({
  items,
  loading = false,
  emptyMessage = '暂无数据',
  renderItem,
  onItemClick,
  selectedItemId,
  className = '',
  enablePhysics = true,
  variant = 'flat',
  spacing = 'comfortable',
  itemPadding,
}: InteractiveListProps<T>) {
  const preset = spacingPresets[spacing];

  if (loading && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
        <span className="text-white/40 text-sm">加载中...</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-12 h-12 mx-auto mb-4 text-white/20">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <p className="text-white/40 font-medium">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ul role="list" className={`${preset.container} ${className}`}>
      {items.map((item, index) => (
        <SpotlightCard
          key={item.id}
          className={`group relative cursor-pointer transition-all duration-300 ${itemPadding || preset.item} ${preset.rounded} ${
            selectedItemId === item.id ? '!bg-white/[0.15]' : 'hover:!bg-white/[0.12]'
          }`}
          enablePhysics={enablePhysics}
          variant={variant}
          shadowOverlay={selectedItemId === item.id ? 'inset 0 2px 4px 0 rgba(255, 255, 255, 0.25)' : undefined}
          sensitivity={spacing === 'compact' ? 0.35 : 0.6}
          onClick={() => onItemClick?.(item)}
        >
          {/* Content */}
          {renderItem(item, index)}
        </SpotlightCard>
      ))}
    </ul>
  );
}

export default InteractiveList;